import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import {
  platformAutomationsApi,
  PlatformMessageTemplate,
} from "../../../cores/platformAutomations/services/api";
import {
  extractVariables,
  stripHtmlToText,
} from "../../../cores/platformAutomations/utils/draftDefinition.utils";
import { useRouterCore } from "../../../cores/router";
import PlatformTemplateComposer from "../../component/platformAutomations/PlatformTemplateComposer";
import Content from "../../layout/Content";
import Layout from "../../layout/Layout";
import {
  EmailTemplateLibrary,
  emailTemplateLibraryFilters,
} from "../emailMarketing/EmailTemplateLibrary";
import { DEFAULT_EMAIL_HTML } from "../emailMarketing/emailHtmlUtils";
import "../emailMarketing/EmailMarketing.scss";

const STATUS_ACCENT: Record<string, string> = {
  published: "#2563eb",
  draft: "#f59e0b",
  archived: "#94a3b8",
};

const formatTemplateMeta = (template: PlatformMessageTemplate) => {
  const updatedAt = template.updatedAt || template.createdAt;
  const dateLabel = updatedAt
    ? new Date(updatedAt).toLocaleDateString("es-CL")
    : template.status;
  return `${template.locale} · ${dateLabel}`;
};

const toLibraryItem = (template: PlatformMessageTemplate) => ({
  id: String(template.id),
  name: template.draftDefinition?.subject || template.name,
  updatedAt: formatTemplateMeta(template),
  accent: STATUS_ACCENT[template.status] || "#64748b",
  filterType: template.status,
});

const readDraftDefinition = (
  template: PlatformMessageTemplate
): PlatformMessageTemplate["draftDefinition"] => {
  const raw = template.draftDefinition as unknown;
  if (!raw) return {};
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) as PlatformMessageTemplate["draftDefinition"];
    } catch {
      return {};
    }
  }
  return raw as PlatformMessageTemplate["draftDefinition"];
};

export default function PlatformMessageTemplatesList() {
  const { goToPlatformAutomations } = useRouterCore();
  const [items, setItems] = useState<PlatformMessageTemplate[]>([]);
  const [filter, setFilter] = useState<"all" | "published" | "draft">("all");
  const [editing, setEditing] = useState<PlatformMessageTemplate | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState(DEFAULT_EMAIL_HTML);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setItems(await platformAutomationsApi.listTemplates());
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const libraryTemplates = useMemo(
    () =>
      (filter === "all"
        ? items
        : items.filter((item) => item.status === filter)
      ).map(toLibraryItem),
    [filter, items]
  );

  const openEditor = (template: PlatformMessageTemplate) => {
    const draft = readDraftDefinition(template);
    setEditing({ ...template, draftDefinition: draft });
    setSubject(draft?.subject || template.name);
    setHtml(draft?.htmlBody || DEFAULT_EMAIL_HTML);
    setComposerOpen(true);
  };

  const openEditorById = async (templateId: string) => {
    try {
      const template = await platformAutomationsApi.getTemplate(Number(templateId));
      openEditor(template);
    } catch {
      const fallback = items.find((item) => String(item.id) === templateId);
      if (fallback) openEditor(fallback);
    }
  };

  const create = async () => {
    const template = await platformAutomationsApi.createTemplate({
      name: "Nuevo email onboarding",
      locale: "es",
      channel: "email",
      draftDefinition: {
        subject: "Hola {{user.firstName}}",
        htmlBody: DEFAULT_EMAIL_HTML,
        textBody: "Hola {{user.firstName}}",
        variables: ["user.firstName"],
      },
    });
    await load();
    openEditor(template);
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const variables = extractVariables(subject, html);
      const updated = await platformAutomationsApi.updateTemplateDraft(
        editing.id,
        {
          name: subject.trim() || editing.name,
          locale: editing.locale,
          draftDefinition: {
            subject,
            htmlBody: html,
            textBody: stripHtmlToText(html),
            variables,
          },
        }
      );
      const draft = readDraftDefinition(updated);
      if (draft?.htmlBody) {
        setHtml(draft.htmlBody);
      }
      setEditing({ ...updated, draftDefinition: draft });
      await load();
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    if (!editing) return;
    await save();
    await platformAutomationsApi.publishTemplate(editing.id);
    await load();
    setComposerOpen(false);
    setEditing(null);
  };

  return (
    <Layout>
      <Content className="email-mkt email-mkt--templates">
        <EmailTemplateLibrary
          eyebrow={
            <FormattedMessage id="app.platformAutomations.templatesLibrary.eyebrow" />
          }
          title={
            <FormattedMessage id="app.emailMarketing.templates.title" />
          }
          subtitle={
            <FormattedMessage id="app.platformAutomations.templatesLibrary.subtitle" />
          }
          statsLabel={
            <FormattedMessage id="app.emailMarketing.templates.stats.total" />
          }
          statsCount={items.length}
          templates={libraryTemplates}
          filters={emailTemplateLibraryFilters.platform}
          activeFilter={filter}
          onFilterChange={(next) =>
            setFilter(next as "all" | "published" | "draft")
          }
          createLabel={
            <FormattedMessage id="app.emailMarketing.templates.create" />
          }
          onCreate={create}
          actionLabel={
            <FormattedMessage id="app.platformAutomations.templatesLibrary.edit" />
          }
          onTemplateAction={openEditorById}
          emptyLabel={
            <FormattedMessage id="app.platformAutomations.templatesLibrary.empty" />
          }
          toolbarExtra={
            <button
              type="button"
              className="email-mkt__ghost-btn"
              onClick={goToPlatformAutomations}
            >
              <FormattedMessage id="app.platformAutomations.templatesLibrary.back" />
            </button>
          }
        />

        <PlatformTemplateComposer
          templateId={editing?.id}
          open={composerOpen && Boolean(editing)}
          name={editing?.name || ""}
          subject={subject}
          html={html}
          saving={saving}
          onNameChange={(value) =>
            editing && setEditing({ ...editing, name: value })
          }
          onSubjectChange={setSubject}
          onHtmlChange={setHtml}
          onClose={() => {
            setComposerOpen(false);
            setEditing(null);
          }}
          onSave={save}
          onPublish={publish}
        />
      </Content>
    </Layout>
  );
}
